// DailyReport.jsx — Updated to use /public/ routes matching your existing pattern
// Drop into your components folder alongside LearningsTab.jsx

import React, { useState, useEffect, useCallback } from 'react';

function StatusBadge({ status }) {
  const config = {
    on_target: { label: '✅ ON TARGET', color: '#00ff41' },
    optimal: { label: '✅ OPTIMAL', color: '#00ff41' },
    stable: { label: '✅ STABLE', color: '#00ff41' },
    slightly_high: { label: '⚠️ SLIGHT HIGH', color: '#ffaa00' },
    slightly_low: { label: '⚠️ SLIGHT LOW', color: '#ffaa00' },
    acceptable: { label: '⚠️ ACCEPTABLE', color: '#ffaa00' },
    minor_fluctuations: { label: '⚠️ MINOR FLUX', color: '#ffaa00' },
    needs_attention: { label: '🔴 ATTENTION', color: '#ff4444' },
    too_high: { label: '🔴 TOO HIGH', color: '#ff4444' },
    too_low: { label: '🔴 TOO LOW', color: '#ff4444' },
    unstable: { label: '🔴 UNSTABLE', color: '#ff4444' },
    oscillating: { label: '🔴 OSCILLATING', color: '#ff4444' },
    critical: { label: '🔴 CRITICAL', color: '#ff4444' },
  };
  const c = config[status] || { label: status?.toUpperCase() || '--', color: '#888' };
  return (
    <span style={{ color: c.color, fontFamily: 'monospace', fontSize: '0.8rem', fontWeight: 'bold' }}>
      {c.label}
    </span>
  );
}

function StabilityMeter({ score }) {
  const getColor = (s) => s >= 8 ? '#00ff41' : s >= 5 ? '#ffaa00' : '#ff4444';
  return (
    <div className="flex items-center gap-2">
      <div className="w-24 h-3 bg-[#1a1a1a] border border-[#333] rounded-sm overflow-hidden">
        <div
          className="h-full transition-all duration-500"
          style={{ width: `${(score || 0) * 10}%`, background: getColor(score) }}
        />
      </div>
      <span className="font-mono font-bold text-sm" style={{ color: getColor(score) }}>
        {score || 0}/10
      </span>
    </div>
  );
}

function CostTracker({ apiUrl, date }) {
  const [cost, setCost] = useState(null);

  useEffect(() => {
    fetch(`${apiUrl}/public/cost-summary/${date}`)
      .then(r => r.ok ? r.json() : null)
      .then(data => setCost(data))
      .catch(() => null);
  }, [apiUrl, date]);

  if (!cost) return null;

  return (
    <div className="terminal-border rounded-lg p-4 mt-4">
      <div className="text-xs text-[#00ff4180] mb-2">💰 API COST — {date}</div>
      <div className="grid grid-cols-3 gap-4">
        <div>
          <div className="text-[10px] text-[#00ff4180]">CALLS</div>
          <div className="text-sm font-bold terminal-glow">
            {cost.total_calls} <span className="text-[#00ff4180] text-xs">({cost.skipped_calls} skipped)</span>
          </div>
        </div>
        <div>
          <div className="text-[10px] text-[#00ff4180]">TODAY</div>
          <div className="text-sm font-bold terminal-glow">
            ${parseFloat(cost.today_cost || 0).toFixed(4)}
          </div>
        </div>
        <div>
          <div className="text-[10px] text-[#00ff4180]">MTD</div>
          <div className="text-sm font-bold terminal-glow">
            ${parseFloat(cost.month_cost || 0).toFixed(2)}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function DailyReport({ apiUrl }) {
  const API_BASE = apiUrl || process.env.REACT_APP_API_URL || 'https://cannaclaude-backend.onrender.com';

  const [report, setReport] = useState(null);
  const [availableDates, setAvailableDates] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch available dates
  useEffect(() => {
    fetch(`${API_BASE}/public/daily-reports`)
      .then(r => r.json())
      .then(data => {
        if (Array.isArray(data)) {
          setAvailableDates(data);
          if (data.length > 0 && !data.find(d => d.report_date === selectedDate)) {
            setSelectedDate(data[0].report_date);
          }
        }
      })
      .catch(err => console.error('Failed to fetch report dates:', err));
  }, [API_BASE]);

  // Fetch selected report
  const fetchReport = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/public/daily-report/${selectedDate}`);
      if (!res.ok) {
        if (res.status === 404) {
          setError('No report generated for this date yet.');
          setReport(null);
        } else {
          throw new Error(`HTTP ${res.status}`);
        }
      } else {
        const data = await res.json();
        const reportData = typeof data.report_data === 'string'
          ? JSON.parse(data.report_data)
          : data.report_data;
        setReport(reportData);
      }
    } catch (err) {
      setError(err.message);
      setReport(null);
    }
    setLoading(false);
  }, [API_BASE, selectedDate]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  if (loading) {
    return (
      <div className="text-center py-12 text-[#00ff4180]">
        <span className="terminal-glow">⟳</span> LOADING DAILY REPORT...
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="terminal-border rounded-lg p-4 flex justify-between items-center flex-wrap gap-3">
        <h2 className="text-lg font-bold terminal-glow">═══ DAILY REPORT ═══</h2>
        <div className="flex gap-2 items-center">
          <select
            className="bg-[#0a0e1a] text-[#00ff41] border border-[#00ff41] rounded px-3 py-2 font-mono text-sm"
            value={selectedDate}
            onChange={e => setSelectedDate(e.target.value)}
          >
            {availableDates.map(d => (
              <option key={d.report_date} value={d.report_date}>
                {d.report_date} — Day {d.grow_day} ({d.stage})
              </option>
            ))}
            {availableDates.length === 0 && (
              <option value={selectedDate}>{selectedDate}</option>
            )}
          </select>
        </div>
      </div>

      {error && !report && (
        <div className="terminal-border rounded-lg p-8 text-center text-[#00ff4180]">
          {error}
        </div>
      )}

      {report && (
        <>
          {/* Grow Info Bar */}
          <div className="terminal-border rounded-lg p-3 flex justify-between items-center flex-wrap gap-3 text-sm">
            <span>📅 {report.date || selectedDate}</span>
            <span>🌱 Day {report.grow_day || '--'}</span>
            <span>📊 <strong>{report.stage?.toUpperCase() || '--'}</strong></span>
            <div className="flex items-center gap-2">
              <span className="text-[#00ff4180] text-xs">STABILITY:</span>
              <StabilityMeter score={report.summary?.stability_score} />
            </div>
          </div>

          {/* Environmental Summary */}
          <div className="terminal-border rounded-lg p-4 scan-line">
            <h3 className="text-sm font-bold mb-3 terminal-glow">📊 24-HOUR ENVIRONMENTAL SUMMARY</h3>

            <div className="space-y-1">
              {[
                { label: 'Day Avg Temp', val: report.summary?.day_temp_avg, unit: '°F', status: report.assessments?.temperature },
                { label: 'Night Avg Temp', val: report.summary?.night_temp_avg, unit: '°F' },
                { label: 'Day Avg RH', val: report.summary?.day_rh_avg, unit: '%', status: report.assessments?.humidity },
                { label: 'Night Avg RH', val: report.summary?.night_rh_avg, unit: '%' },
                { label: 'Avg VPD', val: report.summary?.vpd_avg, unit: ' kPa', status: report.assessments?.vpd },
              ].map((row, i) => (
                <div key={i} className="flex justify-between items-center py-1 border-b border-[#00ff4110]">
                  <span className="text-xs text-[#00ff4180]">{row.label}</span>
                  <div className="flex items-center gap-3">
                    <span className="font-bold text-sm">
                      {row.val != null ? `${row.val}${row.unit}` : '--'}
                    </span>
                    {row.status && <StatusBadge status={row.status} />}
                  </div>
                </div>
              ))}

              {/* Swing indicators */}
              <div className="flex justify-between items-center py-1 border-b border-[#00ff4110]">
                <span className="text-xs text-[#00ff4180]">Temp Swing (24h)</span>
                <span className={`font-bold text-sm ${report.summary?.temp_swing > 15 ? 'text-[#ff4444]' : ''}`}>
                  {report.summary?.temp_swing != null ? `${report.summary.temp_swing}°F` : '--'}
                  {report.summary?.temp_swing > 15 ? ' ⚠️' : ''}
                </span>
              </div>
              <div className="flex justify-between items-center py-1 border-b border-[#00ff4110]">
                <span className="text-xs text-[#00ff4180]">RH Swing (24h)</span>
                <span className={`font-bold text-sm ${report.summary?.rh_swing > 20 ? 'text-[#ff4444]' : ''}`}>
                  {report.summary?.rh_swing != null ? `${report.summary.rh_swing}%` : '--'}
                  {report.summary?.rh_swing > 20 ? ' ⚠️' : ''}
                </span>
              </div>
              <div className="flex justify-between items-center py-1">
                <span className="text-xs text-[#00ff4180]">Oscillation Events</span>
                <span className={`font-bold text-sm ${(report.summary?.oscillation_events || 0) > 0 ? 'text-[#ffaa00]' : ''}`}>
                  {report.summary?.oscillation_events || 0}
                </span>
              </div>
            </div>
          </div>

          {/* Light Guidance */}
          {report.light_recommendation && (
            <div className="terminal-border rounded-lg p-4 scan-line">
              <h3 className="text-sm font-bold mb-3 terminal-glow">💡 LIGHT GUIDANCE</h3>
              <div className="space-y-1">
                {[
                  { label: 'PPFD Target', val: report.light_recommendation.ppfd_target },
                  { label: 'DLI Target', val: report.light_recommendation.dli_target },
                  { label: 'Dimmer', val: report.light_recommendation.dimmer_suggestion },
                  { label: 'Height', val: report.light_recommendation.height_suggestion },
                ].map((row, i) => (
                  <div key={i} className="flex justify-between py-1 border-b border-[#00ff4110]">
                    <span className="text-xs text-[#00ff4180]">{row.label}</span>
                    <span className="text-sm font-bold">{row.val || '--'}</span>
                  </div>
                ))}
              </div>
              {report.light_recommendation.note && (
                <p className="text-xs text-[#00ff4160] mt-2 italic">{report.light_recommendation.note}</p>
              )}
            </div>
          )}

          {/* Daily Checklist */}
          {report.checklist?.length > 0 && (
            <div className="terminal-border rounded-lg p-4 scan-line">
              <h3 className="text-sm font-bold mb-3 terminal-glow">🔍 DAILY CHECKLIST</h3>
              <div className="space-y-2">
                {report.checklist.map((item, i) => (
                  <div key={i} className="flex items-start gap-2 text-xs">
                    <span className="text-[#00ff4140] flex-shrink-0">[ ]</span>
                    <span className="text-[#cccccc]">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Concerns */}
          {report.concerns?.length > 0 && (
            <div className="terminal-border rounded-lg p-4" style={{ borderColor: '#ff4444' }}>
              <h3 className="text-sm font-bold mb-3" style={{ color: '#ff4444' }}>⚠️ CONCERNS</h3>
              <div className="space-y-2">
                {report.concerns.map((item, i) => (
                  <div key={i} className="bg-[#ff444420] border border-[#ff4444] rounded p-2 text-xs text-[#ff8888]">
                    {item}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Recommendations */}
          {report.recommendations?.length > 0 && (
            <div className="terminal-border rounded-lg p-4 scan-line">
              <h3 className="text-sm font-bold mb-3 terminal-glow">💡 RECOMMENDATIONS</h3>
              <div className="space-y-2">
                {report.recommendations.map((item, i) => (
                  <div key={i} className="bg-[#00ff4110] border border-[#00ff4140] rounded p-2 text-xs text-[#aaffaa]">
                    {item}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Cost Tracker */}
          <CostTracker apiUrl={API_BASE} date={selectedDate} />
        </>
      )}
    </div>
  );
}
